import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';

import { toaster } from '@/shared/libs';
import { ConfirmDialog } from '@/shared/ui';
import { useSetProviderStatusMutation } from '@entities/Provider';
import type { Provider } from '@psp-aggregator/sdk';

export interface ResumeProviderModalProps {
  onClose: () => void;
  open: boolean;
  provider: Provider;
}

export const ResumeProviderModal: FC<ResumeProviderModalProps> = ({
  onClose,
  open,
  provider,
}) => {
  const { t } = useTranslation();
  const setStatus = useSetProviderStatusMutation();

  return (
    <ConfirmDialog
      busy={setStatus.isPending}
      cancelLabel={t('buttons.cancel')}
      confirmLabel={t('pages.admin.providers.resume.confirm')}
      description={t('pages.admin.providers.resume.description')}
      open={open}
      title={t('pages.admin.providers.resume.title', {
        name: provider.real_name,
      })}
      tone="success"
      onCancel={onClose}
      onConfirm={() => {
        void (async () => {
          try {
            await setStatus.mutateAsync({
              providerId: provider.id,
              status: 'active',
            });
            toaster.success({ title: t('toast.providerResumed') });
            onClose();
          } catch (error) {
            toaster.error({
              title: isAxiosError(error)
                ? (error.response?.data?.message ??
                  t('errors.providers.statusFailed'))
                : t('errors.unknown'),
            });
          }
        })();
      }}
    />
  );
};

ResumeProviderModal.displayName = 'ResumeProviderModal';
